import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink, RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { ChatReader } from "./kimkim/ChatReader";
import { AiPanel } from "./kimkim/AiPanel";
import { PatternCard } from "./kimkim/PatternCard";
import { ProposalCards } from "./kimkim/ProposalCards";
import { fmtRange } from "./kimkim/BudgetTiers";
import { BUDGET_BANDS, LEAD_TIME_BANDS, RESPONSE_BANDS, GROUP_BANDS, TIER_BANDS, bucketClass, bucketLabel, fmtDate, fmtWhen, fmtUsd, fmtHours } from "./kimkim/labels";

const sel = "bg-white border border-clay-300 px-2 py-1 text-sm outline-none focus:border-terracotta";

function Fact({ label, children, testid }) {
  return <div><div className="smallcaps">{label}</div><div className="mt-0.5 text-sm" data-testid={testid}>{children ?? "—"}</div></div>;
}

export default function KimKimLeadDetail() {
  const { leadId } = useParams();
  const [lead, setLead] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [agents, setAgents] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(() => api.get(`/kimkim/leads/${leadId}`).then(({ data }) => setLead(data)).catch(() => { setNotFound(true); toast.error("Lead no encontrado"); }), [leadId]);
  useEffect(() => { load(); api.get("/kimkim/agent-map").then(({ data }) => setAgents(data.candidates || [])).catch(() => {}); }, [load]);

  const changeAgent = async (email) => {
    try { await api.put(`/kimkim/leads/${leadId}/agent`, { email: email || null }); toast.success("Agente actualizado"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo cambiar"); }
  };
  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data: job } = await api.post(`/kimkim/leads/${leadId}/refresh`);
      toast.success("Releyendo en KimKim…");
      const poll = setInterval(async () => {
        const { data: j } = await api.get(`/kimkim/jobs/${job.job_id}`);
        if (["done", "error"].includes(j.status)) { clearInterval(poll); setRefreshing(false); load(); if (j.status === "error") toast.error(j.error); else toast.success("Lead actualizado"); }
      }, 3000);
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo releer"); setRefreshing(false); }
  };

  if (notFound) return (
    <div className="px-8 py-8 text-sm text-clay-700" data-testid="kimkim-lead-notfound">
      <Link to="/admin/kimkim-leads" className="text-[11px] uppercase tracking-wider text-clay-600 hover:text-clay-900 inline-flex items-center gap-1" data-testid="kimkim-back"><ArrowLeft size={12}/> Leads KimKim</Link>
      <p className="mt-4">Este lead no existe o aún no se ha importado desde KimKim.</p>
    </div>
  );
  if (!lead) return <div className="px-8 py-8 text-sm text-clay-600"><Loader2 size={14} className="animate-spin inline mr-2"/>Cargando…</div>;
  const c = lead.categories || {};
  const timeline = [
    { at: c.requested_at, label: "Solicitud recibida" },
    { at: c.first_reply_at, label: `Primera respuesta nuestra (${fmtHours(c.first_response_hours)})` },
    ...(lead.proposals || []).map((p) => ({ at: p.sent_at, label: `Propuesta${p.price_usd ? ` · ${fmtUsd(p.price_usd)}` : ""}${p.title ? ` · ${p.title}` : ""}` })),
    { at: c.last_message_at, label: "Último mensaje" },
    ...(lead.closed_at ? [{ at: lead.closed_at, label: `Cierre: ${lead.status_raw || bucketLabel(lead.bucket)}` }] : []),
  ].filter((t) => t.at).sort((a, b) => a.at.localeCompare(b.at));

  return (
    <div className="px-8 py-8 max-w-[1500px]" data-testid="kimkim-lead-detail">
      <Link to="/admin/kimkim-leads" className="text-[11px] uppercase tracking-wider text-clay-600 hover:text-clay-900 inline-flex items-center gap-1" data-testid="kimkim-back"><ArrowLeft size={12}/> Leads KimKim</Link>
      <div className="flex flex-wrap items-end justify-between gap-4 mt-3 mb-6">
        <div>
          <div className="smallcaps">Lead KimKim · {lead.kimkim_id}</div>
          <h1 className="font-serif text-4xl leading-none mt-2" data-testid="kimkim-lead-title">{lead.traveler_name || "Viajero"}</h1>
          {lead.trip_name && <div className="mt-2 text-lg text-clay-800" data-testid="kimkim-lead-trip-name">{lead.trip_name}</div>}
          <div className="mt-2 flex items-center gap-2 flex-wrap text-sm text-clay-700">
            <span className={`inline-block border px-2 py-0.5 text-[11px] ${bucketClass(lead.bucket)}`} data-testid="kimkim-lead-bucket">{bucketLabel(lead.bucket)}</span>
            {lead.status_raw && <span className="text-[11px] text-clay-500">KimKim: {lead.status_raw}{lead.close_reason_raw ? ` · ${lead.close_reason_raw}` : ""}{lead.close_comments ? ` · “${lead.close_comments}”` : ""}</span>}
            <span>{(lead.destinations || []).join(", ")}{lead.dates_flexible ? " · fechas aproximadas" : ""}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {lead.url && <a href={lead.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-4 py-2 border border-clay-300 text-[12px] tracking-wider uppercase hover:border-clay-900" data-testid="kimkim-open-url"><ExternalLink size={12}/> Ver en KimKim</a>}
          <button onClick={refresh} disabled={refreshing} className="inline-flex items-center gap-2 px-4 py-2 border border-clay-300 text-[12px] tracking-wider uppercase hover:border-clay-900 disabled:opacity-50" data-testid="kimkim-refresh-lead">
            {refreshing ? <Loader2 size={12} className="animate-spin"/> : <RefreshCw size={12}/>} Releer en KimKim
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_380px] gap-6">
        <div className="space-y-6">
          <section className="border border-clay-300 bg-white p-5 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="kimkim-lead-facts">
            <Fact label="Fechas del viaje">{lead.trip_start ? `${fmtDate(lead.trip_start)}${lead.trip_end ? ` → ${fmtDate(lead.trip_end)}` : " (aprox.)"}` : lead.dates_raw}</Fact>
            <Fact label="Duración">{c.duration_days ? `${c.duration_days} días` : null}</Fact>
            <Fact label="Viajeros">{lead.travelers != null ? `${lead.travelers} (${GROUP_BANDS[c.group_band] || c.group_band})` : null}</Fact>
            <Fact label="Presupuesto" testid="kimkim-lead-budget">
              {c.budget_tier && c.budget_tier !== "sin_categoria" ? <span className="inline-block border border-clay-300 px-1.5 text-[11px] mr-1.5" data-testid="kimkim-lead-tier">{TIER_BANDS[c.budget_tier]}</span> : null}
              {lead.budget_raw || (lead.budget_usd ? fmtUsd(lead.budget_usd) : null)}
              {c.budget_pp_usd ? <div className="text-[11px] text-clay-600">{fmtUsd(c.budget_pp_usd)}/persona · {BUDGET_BANDS[c.budget_pp_band]}{c.budget_pp_day_lo ? ` · ${fmtRange(c.budget_pp_day_lo, c.budget_pp_day_hi)}/pers·día` : c.budget_pp_day ? ` · $${c.budget_pp_day}/pers·día` : ""}</div> : null}
            </Fact>
            <Fact label="Solicitud">{fmtWhen(c.requested_at)}{c.request_month ? <div className="text-[11px] text-clay-600">{c.request_month} · {c.season}</div> : null}</Fact>
            <Fact label="Antelación">{c.lead_time_days != null ? `${c.lead_time_days} días · ${LEAD_TIME_BANDS[c.lead_time_band]}` : LEAD_TIME_BANDS[c.lead_time_band]}</Fact>
            <Fact label="1ª respuesta">{fmtHours(c.first_response_hours)} · {RESPONSE_BANDS[c.first_response_band]}</Fact>
            <Fact label="Mensajes">{c.messages_count} ({c.agent_messages_count} nuestros) · {c.proposals_count} propuestas{c.days_to_close != null ? ` · cierre en ${c.days_to_close} d` : ""}</Fact>
            <Fact label="Especialista en KimKim">{lead.specialist_name}</Fact>
            <Fact label="Agente responsable">
              <select className={sel} value={lead.agent_email || ""} onChange={(e) => changeAgent(e.target.value)} data-testid="kimkim-lead-agent">
                <option value="">Sin asignar</option>
                {agents.map((a) => <option key={a.email} value={a.email}>{a.name}</option>)}
              </select>
              {lead.agent_override && <div className="text-[10px] text-clay-500 mt-0.5">ajustado a mano</div>}
            </Fact>
            <Fact label="Sincronizado">{fmtWhen(lead.synced_at)}</Fact>
          </section>

          {lead.request_text && (
            <details className="border border-clay-300 bg-white p-5" data-testid="kimkim-request-text">
              <summary className="cursor-pointer smallcaps">Petición tal como la hizo el viajero</summary>
              <pre className="mt-3 text-sm whitespace-pre-wrap font-sans text-clay-800 max-h-64 overflow-y-auto">{lead.request_text}</pre>
            </details>
          )}

          <section>
            <div className="smallcaps mb-2">Conversación</div>
            <ChatReader messages={lead.messages} proposals={lead.proposals || []}/>
          </section>

          <ProposalCards proposals={lead.proposals} docs={lead.proposal_docs} budget={c}/>
        </div>

        <div className="space-y-6">
          <section className="border border-clay-300 bg-white p-5" data-testid="kimkim-timeline">
            <div className="smallcaps mb-3">Línea de tiempo</div>
            <ol className="relative border-l border-clay-300 ml-2 space-y-3">
              {timeline.map((t, i) => (
                <li key={i} className="ml-4">
                  <span className="absolute -left-[5px] mt-1.5 h-2 w-2 bg-terracotta"/>
                  <div className="text-[11px] text-clay-500 tabular">{fmtWhen(t.at)}</div>
                  <div className="text-sm">{t.label}</div>
                </li>
              ))}
              {!timeline.length && <li className="ml-4 text-sm text-clay-600">Sin fechas registradas.</li>}
            </ol>
          </section>
          <AiPanel lead={lead} onUpdated={(ai) => setLead({ ...lead, ai })}/>
          <PatternCard pattern={lead.ai_pattern} patternLabels={lead.pattern_labels}/>
        </div>
      </div>
    </div>
  );
}
